import { computed } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { UiIconProps } from "@shopexperts/shared";
import { formatCurrency } from "@/v2Projects/utils/display";
import {
  buildCommissionTooltip,
  formatSignedCurrency,
  getHeadlineAmount,
  getReleaseTransactionIds,
  getWalletEffectAmount,
  getWalletMovement,
  getWalletTimelineState,
  getWalletTransactionHeadline,
  parseExpertLevelCommissionRateBps,
  parseProviderFeeDetails,
  type WalletTransactionLike,
} from "./walletTransactionDisplay";

export interface BreakdownRow {
  key: string;
  label: string;
  value: string;
  tone?: "default" | "deduct" | "total";
  tooltip?: string;
}

export interface EffectTile {
  key: string;
  label: string;
  value: string;
  tooltip?: string;
  icon: UiIconProps["name"];
  iconClass: string;
}

export interface WalletTransactionReference {
  key: string;
  label: string;
  to?: RouteLocationRaw;
}

export interface UseWalletTransactionOptions {
  perspective?: "self" | "admin";
  resolveProjectRoute?: (projectId: number) => RouteLocationRaw | null;
}

export interface FeeBreakdownInput {
  gross_amount_cents: number | null;
  provider_fee_cents: number | null;
  provider_fee_details?: unknown;
  commission_cents: number | null;
  commission_rate_bps?: number | null;
  net_amount_cents: number | null;
  currency: string | null;
}

export function buildFeeBreakdownRows(
  input: FeeBreakdownInput,
  options: Pick<UseWalletTransactionOptions, "perspective"> = {},
): { breakdownRows: BreakdownRow[]; providerFeeLines: ReturnType<typeof parseProviderFeeDetails> } {
  const perspective = options.perspective ?? "self";
  const providerFeeLines = parseProviderFeeDetails(input.provider_fee_details ?? null);

  function money(cents: number): string {
    return formatCurrency(cents, input.currency);
  }

  const rows: BreakdownRow[] = [];
  if (input.gross_amount_cents !== null) {
    rows.push({ key: "gross", label: "Gross amount", value: money(input.gross_amount_cents) });
  }
  if (input.provider_fee_cents !== null) {
    rows.push({
      key: "provider_fee",
      label: "Processing fee",
      value: `−${money(input.provider_fee_cents)}`,
      tone: "deduct",
      tooltip: "Payment processing fee charged by the payment provider.",
    });
  }
  if (input.commission_cents !== null) {
    rows.push({
      key: "commission",
      label: "Platform commission",
      value: `−${money(input.commission_cents)}`,
      tone: "deduct",
      tooltip: buildCommissionTooltip(perspective, input.commission_rate_bps),
    });
  }
  if (input.net_amount_cents !== null) {
    rows.push({
      key: "net",
      label: perspective === "self" ? "Net earnings" : "Net to expert",
      value: money(input.net_amount_cents),
      tone: "total",
    });
  }

  return { breakdownRows: rows, providerFeeLines };
}

interface BuildWalletEffectTilesOptions {
  owner: string;
  money: (cents: number) => string;
  signedMoney: (cents: number) => string;
}

export function buildWalletEffectTiles(
  transaction: WalletTransactionLike,
  options: BuildWalletEffectTilesOptions,
): EffectTile[] {
  const effect = getWalletEffectAmount(transaction);
  const isHoldSource =
    transaction.remaining_hold_cents !== null && transaction.hold_delta_cents > 0;
  const tiles: EffectTile[] = [
    {
      key: "available",
      label: isHoldSource ? "Released" : "Available",
      value: options.signedMoney(effect.availableCents),
      tooltip: isHoldSource
        ? `Funds released from this hold to ${options.owner} available balance.`
        : `How this transaction changed ${options.owner} available balance.`,
      icon: "wallet",
      iconClass: "bg-se-success-light text-se-green",
    },
    {
      key: "hold",
      label: "On hold",
      value: effect.showSignedHold
        ? options.signedMoney(effect.holdCents)
        : options.money(effect.holdCents),
      tooltip: isHoldSource
        ? "Funds remaining in the held balance after this transaction."
        : `How this transaction changed ${options.owner} held balance.`,
      icon: "clock",
      iconClass: "bg-se-warn-light text-se-warn",
    },
  ];

  if (effect.refundedCents > 0) {
    tiles.push({
      key: "refunded",
      label: "Refunded",
      value: `−${options.money(effect.refundedCents)}`,
      tooltip: "Funds refunded directly from this hold.",
      icon: "repeat",
      iconClass: "bg-se-gray-100 text-se-gray-700",
    });
  }

  if (effect.debitedCents > 0) {
    tiles.push({
      key: "debited",
      label: "Debited",
      value: `−${options.money(effect.debitedCents)}`,
      tooltip: "Funds removed from this hold by an admin debit without becoming available.",
      icon: "minus",
      iconClass: "bg-se-danger/10 text-se-danger",
    });
  }

  return tiles;
}

export function useWalletTransaction(
  source: () => WalletTransactionLike,
  options: UseWalletTransactionOptions = {},
) {
  const perspective = options.perspective ?? "self";
  const owner = perspective === "self" ? "your" : "the";

  const transaction = computed(() => source());

  const movement = computed(() => getWalletMovement(transaction.value));
  const tone = computed(() => movement.value.tone);
  const timelineState = computed(() => getWalletTimelineState(transaction.value));
  const headlineAmount = computed(() => getHeadlineAmount(transaction.value, formatCurrency));
  const titleLabel = computed(() => getWalletTransactionHeadline(transaction.value));

  const transactionDate = computed(() => {
    const raw = transaction.value.created_at;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  });

  const fullDate = computed(
    () =>
      transactionDate.value?.toLocaleString("en-US", {
        dateStyle: "full",
        timeStyle: "short",
      }) ?? "Date unavailable",
  );

  const dateLabel = computed(
    () =>
      transactionDate.value?.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) ?? "Date unavailable",
  );

  const timeLabel = computed(
    () =>
      transactionDate.value?.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }) ?? null,
  );

  const dateTimeLabel = computed(() =>
    timeLabel.value ? `${dateLabel.value} • ${timeLabel.value}` : dateLabel.value,
  );

  const releaseTransactionIds = computed(() => getReleaseTransactionIds(transaction.value));

  const releasedByLabel = computed(() => {
    if (releaseTransactionIds.value.length === 0) return null;
    const ids = releaseTransactionIds.value.map((id) => `#${id}`);
    const transactionLabel = ids.length === 1 ? "Transaction" : "Transactions";
    const idList = ids.length === 1 ? ids[0] : `${ids.slice(0, -1).join(", ")} and ${ids[ids.length - 1]}`;
    return `Released by ${transactionLabel} ${idList}`;
  });

  function money(cents: number): string {
    return formatCurrency(cents, transaction.value.currency);
  }

  function signedMoney(cents: number): string {
    return formatSignedCurrency(cents, transaction.value.currency, formatCurrency);
  }

  function optionalMoney(cents: number | null): string | null {
    return cents === null ? null : money(cents);
  }

  const feeBreakdown = computed(() =>
    buildFeeBreakdownRows(
      {
        gross_amount_cents: transaction.value.gross_amount_cents,
        provider_fee_cents: transaction.value.provider_fee_cents,
        provider_fee_details: transaction.value.provider_fee_details,
        commission_cents: transaction.value.commission_cents,
        commission_rate_bps: parseExpertLevelCommissionRateBps(transaction.value.expert_level),
        net_amount_cents:
          transaction.value.transaction_kind === "admin_net_credit" ||
          transaction.value.transaction_kind === "admin_net_debit"
            ? null
            : transaction.value.net_amount_cents,
        currency: transaction.value.currency,
      },
      { perspective },
    ),
  );

  const providerFeeLines = computed(() => feeBreakdown.value.providerFeeLines);
  const breakdownRows = computed(() => feeBreakdown.value.breakdownRows);

  const hasDetails = computed(() => breakdownRows.value.length > 0);

  const effectTiles = computed<EffectTile[]>(() => {
    const t = transaction.value;
    return buildWalletEffectTiles(t, { owner, money, signedMoney });
  });

  const references = computed<WalletTransactionReference[]>(() => {
    const t = transaction.value;
    const refs: WalletTransactionReference[] = [];
    const projectRoute =
      t.project_id !== null ? (options.resolveProjectRoute?.(t.project_id) ?? null) : null;

    if (t.quote_id !== null) {
      refs.push({
        key: "quote",
        label: `Quote #${t.quote_id}`,
        to:
          projectRoute && typeof projectRoute === "object"
            ? {
                ...projectRoute,
                query: { tab: "quotes", quote: String(t.quote_id) },
                hash: `#quote-${t.quote_id}`,
              }
            : undefined,
      });
    }
    if (t.project_id !== null) {
      refs.push({ key: "project", label: `Project #${t.project_id}`, to: projectRoute ?? undefined });
    }
    return refs;
  });

  return {
    transaction,
    movement,
    tone,
    timelineState,
    headlineAmount,
    titleLabel,
    transactionDate,
    fullDate,
    dateLabel,
    timeLabel,
    dateTimeLabel,
    releasedByLabel,
    money,
    signedMoney,
    optionalMoney,
    providerFeeLines,
    breakdownRows,
    hasDetails,
    effectTiles,
    references,
  };
}
